import { isNil, omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

import { DEFAULT_PAGINATION } from 'constants/pagination.constants';
import { PaginationCountAs, PaginationDataAs } from 'enums';
import { useQueryFilters, useQueryPagination } from 'hooks';
import { PaginatedData, PaginationOptions, QueryParams } from 'types';

import { useQuerySorting } from './use-query-sorting.hook';

export type UsePartialQueryResult<TData, TError> = UseQueryResult<
  TData,
  TError
> & {
  paginationCountAs?: PaginationCountAs;
  queryResultCount?: UseQueryResult<PaginatedData<any>>;
  queryCacheCount?: () => void;
  queryExactCount?: () => void;
};

export function usePartialQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  queryKey: QueryKey,
  queryFn: (params: QueryParams) => TQueryFnData | Promise<TQueryFnData>,
  optionsProp?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn'
  > & {
    useCachedCount?: boolean;
    paginated?: boolean;
    /** Overrides the page size a section starts with (e.g. a bulk-action table showing 50). */
    defaultPagination?: PaginationOptions;
  },
): UsePartialQueryResult<TData, TError> {
  const paginated = useMemo(
    () => (isNil(optionsProp?.paginated) ? true : optionsProp?.paginated),
    [optionsProp],
  );

  const { pagination, initialized: paginationInitialized } = useQueryPagination(
    paginated
      ? optionsProp?.defaultPagination || DEFAULT_PAGINATION
      : undefined,
  );
  const { filtersQuery, initialized: queryInitialized } = useQueryFilters();
  const { sorting, initialized: sortingInitialized } = useQuerySorting();

  const options = useMemo(
    () => ({
      ...optionsProp,
      ...((!queryInitialized ||
        !paginationInitialized ||
        !sortingInitialized) && { enabled: false }),
    }),
    [optionsProp, queryInitialized, paginationInitialized, sortingInitialized],
  );

  const queryOptions = useMemo(
    () =>
      omit(options, [
        'useCachedCount',
        'paginated',
        'defaultPagination',
      ]) as Omit<
        UseQueryOptions<TQueryFnData, TError, TData>,
        'queryKey' | 'queryFn'
      >,
    [options],
  );

  const [paginationCountAs, setPaginationCountAs] = useState(
    PaginationCountAs.None,
  );
  const [fetchedPaginationCountAs, setFetchedPaginationCountAs] = useState(
    PaginationCountAs.None,
  );

  const [isLazyCountInternal, setIsLazyCountInternal] = useState(
    options?.useCachedCount,
  );

  const [countResponse, setCountResponse] = useState<Pick<
    PaginatedData<any>,
    'count' | 'pages'
  > | null>(null);

  const queryPagination = useMemo(() => {
    const copy = { ...pagination };
    if (options?.useCachedCount) {
      copy.countAs = PaginationCountAs.None;
    }
    return copy;
  }, [pagination, options?.useCachedCount]);

  const queryCountPagination = useMemo(() => {
    const copy = { ...pagination };
    if (options?.useCachedCount) {
      copy.dataAs = PaginationDataAs.None;
      copy.countAs = paginationCountAs;
    }
    return copy;
  }, [pagination, options?.useCachedCount, paginationCountAs]);

  const queryResult = useQuery(
    [queryKey, filtersQuery, sorting, pagination],
    ({ signal }) =>
      queryFn({
        filters: filtersQuery,
        ...sorting,
        ...queryPagination,
        signal,
      }),
    queryOptions,
  );

  const queryResultCount = useQuery(
    [queryKey, 'count', filtersQuery, pagination?.take, isLazyCountInternal],
    ({ signal }) =>
      queryFn({
        filters: filtersQuery,
        ...queryCountPagination,
        signal,
      }),
    {
      enabled:
        !!options?.useCachedCount && queryInitialized && paginationInitialized,
      onSuccess: (response: any) => {
        setCountResponse({
          count: response.count,
          pages: response.pages,
        });
        setFetchedPaginationCountAs(response.countAs);
        setPaginationCountAs(response.countAs);
      },
    },
  );

  const queryCacheCount = useCallback(() => {
    setIsLazyCountInternal(false);
    setPaginationCountAs(PaginationCountAs.Cache);
  }, []);

  const queryExactCount = useCallback(() => {
    setIsLazyCountInternal(false);
    setPaginationCountAs(PaginationCountAs.Exact);
  }, []);

  return {
    ...queryResult,
    data: isNil(queryResult.data)
      ? undefined
      : ({
          ...queryResult.data,
          ...countResponse,
        } as unknown as any), // TODO: fix typings
    ...(options?.useCachedCount && {
      paginationCountAs: fetchedPaginationCountAs,
      queryResultCount,
      queryCacheCount,
      queryExactCount,
    }),
  };
}
